import React, { useState } from 'react';
import { Search, CheckCircle, AlertCircle, Loader2, ChevronLeft, Calendar } from 'lucide-react';
import { useCheckPromoterEmailMutation } from '../../../store/vendorApi';
import type { CheckPromoterEmailResponse } from '@clareefai/types';

interface EmailSearchStepProps {
  onPromoterFound: (data: CheckPromoterEmailResponse) => void;
  onBack: () => void;
  onCancel: () => void;
}

const EmailSearchStep: React.FC<EmailSearchStepProps> = ({ onPromoterFound, onBack, onCancel }) => {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<CheckPromoterEmailResponse | null>(null);
  const [checkEmail, { isLoading }] = useCheckPromoterEmailMutation();

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleCheck = async () => {
    try {
      const data = await checkEmail({ email }).unwrap();
      setResult(data);
    } catch {
      setResult({ found: false, promoter_id: null, promoter_name: null, promoter_email: null, promoter_company: null, member_since: null });
    }
  };

  const handleSelect = () => {
    if (result?.found) {
      onPromoterFound(result);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Search by email address</h3>
        <p className="text-gray-500">Find the promoter in your network</p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setResult(null); }}
              placeholder="promoter@company.com"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
              onKeyDown={(e) => { if (e.key === 'Enter' && isValidEmail) handleCheck(); }}
            />
          </div>
        </div>

        <button
          onClick={handleCheck}
          disabled={!isValidEmail || isLoading}
          className="w-full py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Check Email
        </button>

        {result?.found && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-800">Contact Found!</span>
            </div>
            <div className="space-y-2 text-sm">
              <p className="text-gray-700"><span className="font-medium">Name:</span> {result.promoter_name}</p>
              <p className="text-gray-700"><span className="font-medium">Email:</span> {result.promoter_email}</p>
              {result.promoter_company && (
                <p className="text-gray-700"><span className="font-medium">Company:</span> {result.promoter_company}</p>
              )}
              {result.member_since && (
                <p className="text-gray-500 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Member since {new Date(result.member_since).toLocaleDateString()}
                </p>
              )}
            </div>
            <button
              onClick={handleSelect}
              className="w-full py-2.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors font-medium text-sm"
            >
              Select and Confirm Contact
            </button>
          </div>
        )}

        {result && !result.found && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-700">No promoter found with this email. Go back and choose "No" to invite them.</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="flex items-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </button>
        <button onClick={onCancel} className="px-6 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EmailSearchStep;
